/**
 * Manages a Remotion project on the user's local disk.
 *
 * The project lives in a temp directory (or a user-specified directory).
 * It contains:
 *   src/index.tsx              — Remotion entry point
 *   src/SibrikComposition.tsx — the composition component
 *   public/media/             — downloaded video/audio/image files
 *   package.json              — minimal Remotion project config
 *
 * The state (clips, text, audio) is kept in memory and persisted to
 * sibrik-project.json so the user can resume across sessions.
 */

import { promises as fs } from 'node:fs';
import path from 'node:path';
import os from 'node:os';
import { exec } from 'node:child_process';
import {
  type CompositionState,
  type VideoClip,
  type TextLayer,
  type AudioTrack,
  generateEntryFile,
  generateComposition,
  generateProjectPackageJson,
} from './templates.js';

const DEFAULT_FPS = 30;
const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;
const PROJECT_FILE = 'sibrik-project.json';

export class ProjectManager {
  private projectDir: string;
  private state: CompositionState;
  private initialized = false;

  constructor(projectDir?: string) {
    this.projectDir =
      projectDir ?? path.join(os.tmpdir(), 'sibrik-editor-' + Date.now());
    this.state = {
      fps: DEFAULT_FPS,
      width: DEFAULT_WIDTH,
      height: DEFAULT_HEIGHT,
      durationFrames: 1,
      clips: [],
      textLayers: [],
      audioTracks: [],
    };
  }

  get dir(): string {
    return this.projectDir;
  }

  get mediaDir(): string {
    return path.join(this.projectDir, 'public', 'media');
  }

  get compositionState(): Readonly<CompositionState> {
    return this.state;
  }

  /** Creates the project directory structure and writes initial files. */
  async init(): Promise<void> {
    await fs.mkdir(path.join(this.projectDir, 'src'), { recursive: true });
    await fs.mkdir(this.mediaDir, { recursive: true });
    await fs.writeFile(
      path.join(this.projectDir, 'package.json'),
      generateProjectPackageJson(),
    );
    await this.writeCompositionFiles();
    await this.saveState();
    await this.ensureDependencies();
    this.initialized = true;
  }

  /** Loads a previously saved project from disk. */
  async load(): Promise<boolean> {
    try {
      const raw = await fs.readFile(
        path.join(this.projectDir, PROJECT_FILE),
        'utf-8',
      );
      this.state = JSON.parse(raw);
      this.initialized = true;
      await this.ensureDependencies();
      return true;
    } catch {
      return false;
    }
  }

  /** Persists the composition state to disk. */
  async saveState(): Promise<void> {
    await fs.writeFile(
      path.join(this.projectDir, PROJECT_FILE),
      JSON.stringify(this.state, null, 2),
    );
  }

  /** Writes the entry file and composition component from current state. */
  async writeCompositionFiles(): Promise<void> {
    await fs.writeFile(
      path.join(this.projectDir, 'src', 'index.tsx'),
      generateEntryFile(this.state),
    );
    await fs.writeFile(
      path.join(this.projectDir, 'src', 'SibrikComposition.tsx'),
      generateComposition(this.state),
    );
  }

  /** Adds a video clip at the end (or at a specified frame). */
  async addVideoClip(
    localPath: string,
    durationFrames: number,
    startFrame?: number,
  ): Promise<VideoClip> {
    const start = startFrame ?? this.state.durationFrames;
    const isImage = /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(localPath);
    const clip: VideoClip = { localPath, startFrame: start, durationFrames, type: isImage ? 'image' : 'video' };
    this.state.clips.push(clip);
    this.recalculateDuration();
    await this.writeCompositionFiles();
    await this.saveState();
    return clip;
  }

  /** Adds a text overlay. */
  async addTextLayer(layer: Omit<TextLayer, 'startFrame' | 'durationFrames'> & {
    startFrame?: number;
    durationFrames?: number;
  }): Promise<TextLayer> {
    const textLayer: TextLayer = {
      text: layer.text,
      startFrame: layer.startFrame ?? 0,
      durationFrames: layer.durationFrames ?? this.state.fps * 3,
      fontSize: layer.fontSize,
      color: layer.color,
      position: layer.position,
    };
    this.state.textLayers.push(textLayer);
    await this.writeCompositionFiles();
    await this.saveState();
    return textLayer;
  }

  /** Adds an audio track. */
  async addAudioTrack(
    localPath: string,
    durationFrames: number,
    startFrame?: number,
    volume?: number,
  ): Promise<AudioTrack> {
    const track: AudioTrack = {
      localPath,
      startFrame: startFrame ?? 0,
      durationFrames,
      volume,
    };
    this.state.audioTracks.push(track);
    await this.writeCompositionFiles();
    await this.saveState();
    return track;
  }

  /** Removes a clip by index and re-packs the timeline (shifts subsequent clips to fill the gap). */
  async removeVideoClip(index: number): Promise<void> {
    const removed = this.state.clips[index];
    this.state.clips.splice(index, 1);

    // Re-pack: shift all clips that started after the removed clip's start
    const removedDuration = removed.durationFrames;
    for (let i = index; i < this.state.clips.length; i++) {
      this.state.clips[i].startFrame -= removedDuration;
    }

    this.recalculateDuration();
    await this.writeCompositionFiles();
    await this.saveState();
  }

  /** Removes a text layer by index. */
  async removeTextLayer(index: number): Promise<void> {
    this.state.textLayers.splice(index, 1);
    await this.writeCompositionFiles();
    await this.saveState();
  }

  /** Removes an audio track by index. */
  async removeAudioTrack(index: number): Promise<void> {
    this.state.audioTracks.splice(index, 1);
    await this.writeCompositionFiles();
    await this.saveState();
  }

  /** Sets the video resolution. */
  async setResolution(width: number, height: number): Promise<void> {
    this.state.width = width;
    this.state.height = height;
    await this.writeCompositionFiles();
    await this.saveState();
  }

  /** Sets the FPS. */
  async setFps(fps: number): Promise<void> {
    this.state.fps = fps;
    await this.writeCompositionFiles();
    await this.saveState();
  }

  /** Lists all media files in the project's public/media/ folder. */
  async listMedia(): Promise<
    Array<{ filename: string; relativePath: string; size: number }>
  > {
    try {
      const files = await fs.readdir(this.mediaDir);
      const result: Array<{ filename: string; relativePath: string; size: number }> = [];
      for (const filename of files) {
        const fullPath = path.join(this.mediaDir, filename);
        const stat = await fs.stat(fullPath);
        if (stat.isFile()) {
          result.push({
            filename,
            relativePath: `media/${filename}`,
            size: stat.size,
          });
        }
      }
      return result;
    } catch {
      return [];
    }
  }

  /** Returns the full path for a media filename. */
  mediaPath(filename: string): string {
    return path.join(this.mediaDir, filename);
  }

  /** Returns the relative path (from public/) for a media filename. */
  mediaRelativePath(filename: string): string {
    return `media/${filename}`;
  }

  /** Returns project info as a plain object. */
  getInfo() {
    return {
      projectDir: this.projectDir,
      fps: this.state.fps,
      width: this.state.width,
      height: this.state.height,
      durationFrames: this.state.durationFrames,
      durationSeconds: Math.round((this.state.durationFrames / this.state.fps) * 10) / 10,
      clips: this.state.clips,
      textLayers: this.state.textLayers,
      audioTracks: this.state.audioTracks,
    };
  }

  /** Installs npm dependencies if node_modules is missing. */
  private async ensureDependencies(): Promise<void> {
    const nodeModulesPath = path.join(this.projectDir, 'node_modules');
    try {
      await fs.access(nodeModulesPath);
      return;
    } catch {
      // node_modules doesn't exist — run npm install
    }

    return new Promise((resolve, reject) => {
      const child = exec('npm install', {
        cwd: this.projectDir,
        timeout: 120_000,
      });
      child.on('close', (code) => {
        if (code === 0) resolve();
        else reject(new Error(`npm install failed with code ${code}`));
      });
      child.on('error', (err) => reject(err));
    });
  }

  /** Recalculates total duration based on all clips and audio. */
  private recalculateDuration(): void {
    let maxEnd = 0;
    for (const clip of this.state.clips) {
      const end = clip.startFrame + clip.durationFrames;
      if (end > maxEnd) maxEnd = end;
    }
    for (const track of this.state.audioTracks) {
      const end = track.startFrame + track.durationFrames;
      if (end > maxEnd) maxEnd = end;
    }
    this.state.durationFrames = maxEnd;
  }
}
